import { Injectable } from "@angular/core";
import { ModalService } from "@core-services/modal.service";
import { FiatTransaction, SendTransaction } from "@models/transaction";
import { SendModalComponent } from "@modules/main/components/step-summary/sending-modal/send-modal/send-modal.component";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import {
	DISABLED_REASONS,
	disabledTooltipTranslations,
	SEND_DISABLED_TOOLTIP_TRANSLATIONS
} from "@modules/+new/payments/features/send/entities/disabled-tooltip-translations.registry";
import { UserService } from "@main-services/user.service";
import { WalletsBalancesFacadeService } from "@modules/+new/core/wallets-balances/domain/+state/wallets-balances.facade.service";
import { map, switchMap, take } from "rxjs/operators";
import { WalletBalance } from "@modules/+new/core/wallets-balances/domain/entities/wallet-balance.model";
import { Observable, combineLatest, of } from "rxjs";
import { Fiat, FiatWallet, PayoutProvider } from "@models";
import { MasterdataService } from "@core-services/masterdata.service";
import { FiatService } from "@main-services/currency";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { Tradable } from "@models/interfaces/tradable";
import { WalletsBalanceStatus } from "@modules/+new/core/wallets-balances/domain/entities/wallets-balance-status.model";
import { WalletBalanceStatus } from "@modules/+new/core/wallets-balances/domain/entities/wallet-balance-status.model";
import { CashplusPortfolioFacade } from "@modules/cashplus/+state/cashplus-portfolio.facade";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { SendSelectNetworkComponent } from "@modules/main/components/step-summary/sending-modal/send-select-network/send-select-network.component";
import { SendWarningComponent } from "@modules/main/components/step-summary/sending-modal/send-warning/send-warning.component";
import { CryptocoinsState } from "@modules/+new/payments/shared-payments/services/cryptocoins-state.service";
import { TinShieldEntryHelper } from "@modules/taxes/tin/services/tin-shield-entry.helper";

export interface ButtonStatus {
	readonly isDisabled: boolean;
	readonly message: SEND_DISABLED_TOOLTIP_TRANSLATIONS;
}

@Injectable({
	providedIn: "root"
})
export class SendEntryPointService {
	constructor(
		private readonly modalService: ModalService,
		private readonly tradableWalletListService: TradableWalletListService,
		private readonly userService: UserService,
		private readonly walletBalanceFacade: WalletsBalancesFacadeService,
		private readonly masterdataService: MasterdataService,
		private readonly fiatService: FiatService,
		private readonly fiatWalletService: FiatWalletService,
		private readonly cashPlusPortfolioFacade: CashplusPortfolioFacade,
		private readonly cashPlusFacade: CashPlusFacade,
		protected readonly cryptocoinsState: CryptocoinsState,
		private readonly tinShieldEntryHelper: TinShieldEntryHelper
	) {}

	public openFiatSendModal(fiatTransaction: FiatTransaction): void {
		this.tinShieldEntryHelper
			.handleTinStatusCheck$()
			.pipe(take(1))
			.subscribe({
				next: (isTinFrozen) => {
					if (isTinFrozen) {
						return;
					}
					this.modalService
						.create(SendModalComponent, {
							fiatTx: fiatTransaction,
							paymentsFlow: true,
							data: { transaction: new SendTransaction() }
						})
						.present();
				}
			});
	}

	public openMultichainSendFlow(wallet: TradableWallet, tradable: Tradable): void {
		combineLatest({
			tinFrozen: this.tinShieldEntryHelper.handleTinStatusCheck$(),
			list: this.cryptocoinsState.availableCryptoWithdrawalList$
		})
			.pipe(take(1))
			.subscribe(({ tinFrozen, list }) => {
				if (tinFrozen) {
					return;
				}
				const currentCrypto = list.find(
					(item) => item.symbol.toUpperCase() === tradable.getAssetSymbol().toUpperCase()
				);

				if (currentCrypto.networks.length > 1) {
					this.modalService
						.create(SendSelectNetworkComponent, {
							availableNetworks: currentCrypto.networks.filter(
								(network) => network.is_withdraw_allowed && network.is_operational
							),
							unavailableNetworks: currentCrypto.networks.filter(
								(network) => network.is_withdraw_allowed && !network.is_operational
							),
							selectedCryptocoin: currentCrypto,
							walletId: wallet.getId()
						})
						.present();
				} else {
					this.modalService
						.create(SendWarningComponent, {
							selectedCryptocoin: currentCrypto,
							walletId: wallet.getId(),
							selectedNetwork: currentCrypto.networks[0]
						})
						.present();
				}
			});
	}

	public openCryptoSendModal(
		data: { transaction: SendTransaction },
		props?: {
			perfectInputMatch?: boolean;
			forceHideFiatSend?: boolean;
		}
	): void {
		this.tinShieldEntryHelper
			.handleTinStatusCheck$()
			.pipe(take(1))
			.subscribe({
				next: (isTinFrozen) => {
					if (isTinFrozen) {
						return;
					}
					const inputs = { data, ...props };
					this.modalService.create(SendModalComponent, inputs, { showCloseButton: false }).present();
				}
			});
	}

	public getStatusForFiatWallet$(fiatWalletId: string): Observable<ButtonStatus> {
		const fiatWallet: FiatWallet = this.fiatWalletService.getItemById(fiatWalletId);
		const fiatId = fiatWallet.getCurrencyId();
		const fiat = this.fiatService.getItemById(fiatId);

		return combineLatest([
			this.checkIfCashPlusAssetAboveMinimum$(+fiat.getMinWalletWithdraw(), fiat.getPid()),
			this.getFiatWalletBalanceStatus$(fiatWalletId)
		]).pipe(
			map(([isCashPlusAssetAvailable, balanceStatus]) => {
				const { balance, hasUnconfirmedBalance }: WalletBalanceStatus = balanceStatus;
				return this.getStatusForFiatWallet(fiat, balance, hasUnconfirmedBalance, isCashPlusAssetAvailable);
			})
		);
	}

	private getStatusForFiatWallet(
		fiat: Fiat,
		balance: WalletBalance,
		hasUnconfirmedBalance: boolean,
		isCashPlusAssetAvailable = false
	): ButtonStatus {
		const minSendAmount = +fiat.getMinWalletWithdraw();

		if (this.userService.user.isUnverified()) {
			return this.getStatus(DISABLED_REASONS.USER_NOT_VERIFIED);
		}

		if (!this.fiatHaveSendProvider(fiat.getId())) {
			return this.getStatus(DISABLED_REASONS.FIAT_SEND_UNAVAILABLE);
		}

		if (this.isTradableWalletValid(balance, minSendAmount)) {
			if (hasUnconfirmedBalance) {
				return this.getStatus(DISABLED_REASONS.RECENT_DEPOSIT_NEEDS_CONFIRMATION);
			}

			if (isCashPlusAssetAvailable) {
				return this.getStatus(DISABLED_REASONS.CASHPLUS_BALANCE_UNUSED);
			}

			return this.getStatus(DISABLED_REASONS.WALLET_BALANCE_UNDER_MIN_AMOUNT);
		}

		return this.getStatus();
	}

	public getSendStatusForCryptoCoin(
		coin: Tradable,
		balance: WalletBalance,
		hasUnconfirmedBalance: boolean
	): ButtonStatus {
		const minSendAmount: number = coin.getAssetSettings().getMinWithdrawal();

		if (this.userService.isVerificationMandatory) {
			return this.getStatus(DISABLED_REASONS.USER_NOT_VERIFIED);
		}

		if (!coin.isAvailable()) {
			return this.getStatus(DISABLED_REASONS.UNDER_MAINTENANCE);
		}
		if (!coin.isWithdrawActive()) {
			return this.getStatus(DISABLED_REASONS.WITHDRAW_DISABLED);
		}

		if (this.isTradableWalletValid(balance, minSendAmount)) {
			return this.getStatus(
				hasUnconfirmedBalance
					? DISABLED_REASONS.RECENT_DEPOSIT_NEEDS_CONFIRMATION
					: DISABLED_REASONS.NO_WALLET_WITH_ENOUGH_BALANCE
			);
		}

		return this.getStatus();
	}

	public getSendStatusForCryptoCoin$(coin: Tradable): Observable<ButtonStatus> {
		return this.highestCoinWalletBalanceStatus$(coin.getId()).pipe(
			map(({ balance, hasUnconfirmedBalance }: WalletBalanceStatus) =>
				this.getSendStatusForCryptoCoin(coin, balance, hasUnconfirmedBalance)
			)
		);
	}

	public getFiatWalletBalanceStatus$(walletId: string): Observable<WalletBalanceStatus> {
		return this.walletBalanceFacade.getFiatBalancesStatus$().pipe(
			map((status: WalletsBalanceStatus) => ({
				hasUnconfirmedBalance: status.hasUnconfirmedBalance,
				balance: status.balances.find((balance: WalletBalance) => balance.walletGuid === walletId)
			}))
		);
	}

	// cashplus send or withdraw check helper for parked asset
	public checkIfCashPlusAssetAboveMinimum$(amount: number, fiatPid: string): Observable<boolean> {
		return this.cashPlusFacade.isCashPlusEnabled$().pipe(
			switchMap((enabled) => {
				if (enabled) {
					return this.cashPlusPortfolioFacade.getPortfolioPerFiatState$(fiatPid).pipe(
						map((val) => {
							if (val) {
								return +val.parked_asset_balance > amount;
							}

							return false;
						})
					);
				}

				return of(false);
			})
		);
	}

	private highestCoinWalletBalanceStatus$(coinId: string): Observable<WalletBalanceStatus> {
		const walletId: string = this.tradableWalletListService.getHighestBalanceWallet(coinId).getId();

		return this.walletBalanceFacade.getCryptoBalancesStatus$().pipe(
			map((status: WalletsBalanceStatus) => ({
				...status,
				balance: status.balances.find((balance: WalletBalance) => balance.walletGuid === walletId)
			}))
		);
	}

	private isTradableWalletValid(walletBalance: WalletBalance, minBalance: number = 0): boolean {
		return !!walletBalance && walletBalance.operationalBalance < minBalance;
	}

	private getStatus(reason?: DISABLED_REASONS): ButtonStatus {
		return {
			isDisabled: reason !== undefined,
			message: reason !== undefined ? this.getTranslationForReason(reason) : null
		};
	}

	private fiatHaveSendProvider(fiatId: string): boolean {
		return this.masterdataService.payout_providers.some(
			(p: PayoutProvider) =>
				p.sendEnabled(true) && p.isEnabled() && p.getFiatIDs().some((_fiatId: string) => _fiatId === fiatId)
		);
	}

	private getTranslationForReason = (reason: DISABLED_REASONS): SEND_DISABLED_TOOLTIP_TRANSLATIONS =>
		disabledTooltipTranslations.get(reason);
}
